<template>
  <ItemPage
    :title="!questionId? 'Criar questão' : `Editando questão ${this.questionId}`"
    size="lg"
    :actions="actions"
  >
    <v-progress-linear
      v-if="loading"
      color="amber"
      indeterminate
    />

    <div v-show="!loading">
      <v-row
        class="mt-6"
        align="center"
        no-gutters
      >
        <v-col
          cols="12"
          sm="6"
        >
          <div class="font-weight-bold">
            Tags
          </div>
          <div class="text-body-2 text--secondary mt-1">
            As tags serão utilizadas para relacionar conteúdos com características em comum.
          </div>
        </v-col>

        <v-col
          class="ml-auto"
          cols="auto"
        >
          <v-btn
            color="primary"
            text
            @click="openAddTagDialog"
          >
            <v-icon left>
              mdi-plus
            </v-icon> Adicionar Tag
          </v-btn>
        </v-col>
      </v-row>
      <v-simple-table
        v-if="question.tags.length > 0"
      >
        <thead>
          <tr>
            <th>Nome da Tag</th>
            <th class="text-right" />
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="tag in question.tags"
            :key="tag.id"
          >
            <td> {{ tag.name }}</td>

            <td class="text-right">
              <v-btn
                small
                text
                @click="deleteExamQuestionTags(tag.id)"
              >
                <v-icon
                  small
                  left
                >
                  mdi-delete
                </v-icon>
                Remover
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <v-divider class="my-10" />

      <div class="title">
        Enunciado
      </div>
      <div class="editor elevation-2 mt-3 white">
        <editor-menu-bar
          v-slot="{ commands, isActive, getNodeAttrs }"
          :editor="statementEditor"
        >
          <v-toolbar
            flat
            dense
          >
            <v-btn
              icon
              tile
              :input-value="isActive.bold()"
              :color="isActive.bold() ? 'primary' : ''"
              @click="commands.bold()"
            >
              <v-icon>
                mdi-format-bold
              </v-icon>
            </v-btn>

            <v-btn
              icon
              tile
              :input-value="isActive.italic()"
              :color="isActive.italic() ? 'primary' : ''"
              @click="commands.italic()"
            >
              <v-icon>
                mdi-format-italic
              </v-icon>
            </v-btn>

            <v-btn
              icon
              tile
              :input-value="isActive.exam_question_image()"
              :color="isActive.exam_question_image() ? 'primary' : ''"
              @click="handleImageCommand(commands.exam_question_image)"
            >
              <v-icon>
                mdi-image
              </v-icon>
            </v-btn>

            <v-btn
              icon
              tile
              :input-value="isActive.exam_question_katex()"
              :color="isActive.exam_question_katex() ? 'primary' : ''"
              @click="handleKatexCommand(commands, isActive.exam_question_katex(), getNodeAttrs)"
            >
              <v-icon>
                mdi-square-root
              </v-icon>
            </v-btn>
          </v-toolbar>
        </editor-menu-bar>
        <v-divider />
        <div>
          <editor-content
            class="editor-content pa-5"
            :editor="statementEditor"
          />
        </div>
      </div>
      <div class="mt-12 d-flex align-center">
        <div class="title">
          Respostas
        </div>

        <v-spacer />

        <v-btn
          color="primary"
          text
          @click="addAnswer"
        >
          <v-icon left>
            mdi-plus
          </v-icon>
          Adicionar resposta
        </v-btn>
      </div>

      <template v-if="answers.length > 0">
        <div
          v-for="(answer, index) in answers"
          :key="index"
          class="mt-8"
        >
          <div class="d-flex justify-space-between">
            <div>Resposta {{ index + 1 }}</div>
            <v-btn
              v-if="!answer.is_correct"
              class="text-decoration-none"
              text
              @click="setCorrectAnswer(index)"
            >
              Marcar como correta
            </v-btn>

            <div
              v-else
              class="success--text"
              style="min-height: 36px;"
            >
              <v-icon color="success">
                mdi-check
              </v-icon>
              Correta
            </div>
          </div>
          <div class="mt-2 editor elevation-2 white">
            <editor-menu-bar
              v-slot="{ commands, isActive, getNodeAttrs }"
              :editor="answer.editor"
            >
              <v-toolbar
                flat
                dense
              >
                <v-btn
                  icon
                  tile
                  :input-value="isActive.bold()"
                  :color="isActive.bold() ? 'primary' : ''"
                  @click="commands.bold()"
                >
                  <v-icon>
                    mdi-format-bold
                  </v-icon>
                </v-btn>

                <v-btn
                  icon
                  tile
                  :input-value="isActive.italic()"
                  :color="isActive.italic() ? 'primary' : ''"
                  @click="commands.italic()"
                >
                  <v-icon>
                    mdi-format-italic
                  </v-icon>
                </v-btn>

                <v-btn
                  icon
                  tile
                  :input-value="isActive.exam_question_image()"
                  :color="isActive.exam_question_image() ? 'primary' : ''"
                  @click="handleImageCommand(commands.exam_question_image)"
                >
                  <v-icon>
                    mdi-image
                  </v-icon>
                </v-btn>

                <v-btn
                  icon
                  tile
                  :input-value="isActive.exam_question_katex()"
                  :color="isActive.exam_question_katex() ? 'primary' : ''"
                  @click="handleKatexCommand(commands, isActive.exam_question_katex(), getNodeAttrs)"
                >
                  <v-icon>
                    mdi-square-root
                  </v-icon>
                </v-btn>

                <v-spacer />

                <v-btn
                  icon
                  tile
                  @click="showRemoveAnswerModal(index)"
                >
                  <v-icon>
                    mdi-delete
                  </v-icon>
                </v-btn>
              </v-toolbar>
            </editor-menu-bar>
            <v-divider />
            <div>
              <editor-content
                class="editor-content pa-5"
                :editor="answer.editor"
              />
            </div>
          </div>
        </div>
      </template>
      <div
        v-else
        class="grey--text text-center my-5"
      >
        Nenhuma reposta.
      </div>
    </div>
  </ItemPage>
</template>

<script>
import AddTagDialog from '@/components/contents/questions/AddTagDialog.vue'
import KatexDialog from '@/components/KatexDialog.vue'
import ImageDialog from '@/components/ImageDialog.vue'
import ItemPage from '@/components/pages/ItemPage.vue'
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import {
  HardBreak,
  Bold,
  Italic,
  History
} from 'tiptap-extensions'

import Image from '../nodes/Image'
import Katex from '../nodes/Katex'
// import { makeid } from '@/util.js'

export default {
  components: {
    EditorContent,
    EditorMenuBar,
    ItemPage
  },

  data: () => ({
    question: null,
    statementEditor: new Editor({
      extensions: [
        new HardBreak(),
        new Image(),
        new Bold(),
        new Italic(),
        new History(),
        new Katex()
      ],
      content: {
        type: 'doc',
        content: [
          {
            type: 'paragraph'
          }
        ]
      }
    }),
    answers: [],
    imageCommand: null,

    // Katex extension
    katexCommand: null,
    katexInput: null,
    katexUpdating: false,
    invalidKatex: true,
    katexPreview: '',

    selectedImage: null,
    isFetching: true,
    correctAnswerId: null,
    loading: false,
    foreign_language: 0,
    foreign_language_items: [
      {
        name: 'Nenhuma',
        value: 0
      },
      {
        name: 'Língua inglesa',
        value: 1
      },
      {
        name: 'Língua espanhola',
        value: 2
      }
    ],
    topicName: ''
  }),
  computed: {
    examId () {
      return this.$route.params.examId
    },
    groupId () {
      return this.$route.params.groupId
    },
    questionId () {
      return this.$route.params.questionId
    },

    actions () {
      return [
        {
          label: this.id ? 'Atualizar' : 'Salvar',
          action: this.submit,
          icon: 'mdi-floppy',
          disabled: false
          // disabled: this.published ? this.published : this.loading
        },
        {
          label: 'Deletar',
          action: this.deleteExamQuestions,
          icon: 'mdi-delete',
          disabled: false
          // disabled: this.published
        }
      ]
    }
  },

  async mounted () {
    if (!this.questionId) {
      return
    }
    await this.buildQuestion()
  },

  beforeDestroy () {
    this.statementEditor.destroy()
    for (const answer of this.answers) {
      answer.editor.destroy()
    }
  },

  methods: {
    async buildQuestion () {
      this.loading = true
      this.answers = []

      const res = await this.$api.getExamQuestions(this.questionId)
      this.question = res

      const statement = JSON.parse(this.question.statement)
      this.statementEditor.setContent(statement, true)

      const answers = this.question.options

      for (const answer of answers) {
        answer.editor = new Editor({
          // editable: !this.published,
          extensions: [
            new HardBreak(),
            new Image(),
            new Bold(),
            new Italic(),
            new History(),
            new Katex()
          ],
          content: JSON.parse(answer.text)
        })
        this.answers.push(answer)
      }
      this.loading = false
    },
    // Image Extension
    handleImageCommand (command) {
      this.openImageDialog()
      this.imageCommand = command
    },

    openImageDialog () {
      this.$dialog.custom(ImageDialog).show()
        .then(async (data) => {
          if (!data) {
            return
          }
          try {
            const uploadedImageData = await this.$api.uploadExamQuestionImage(this.questionId, data.image, data.size)
            this.imageCommand({
              src: uploadedImageData.url,
              examQuestionImageId: uploadedImageData.id
            })
          } catch {

          }
        })
    },

    // Katex Extension
    handleKatexCommand (commands, isActive, getNodeAttrs) {
      if (!isActive) {
        this.katexUpdating = false
        this.katexInput = ''
        this.katexCommand = commands.exam_question_katex_createNode
      } else {
        this.katexUpdating = true
        const { expression } = getNodeAttrs('exam_question_katex')
        this.katexInput = expression
        this.katexCommand = commands.exam_question_katex_updateNode
      }
      this.openKatexDialog()
    },

    openKatexDialog () {
      this.$dialog.custom(KatexDialog).with({ kInput: this.katexInput }).show()
        .then((katex) => {
          if (!katex) {
            return
          }
          this.onSaveKatexClick(katex)
        })
    },

    onSaveKatexClick (katex) {
      this.katexCommand({
        expression: katex
      })
    },

    addAnswer () {
      const editor = new Editor({
        extensions: [
          new HardBreak(),
          new Image(),
          new Bold(),
          new Italic(),
          new History(),
          new Katex()
        ],
        content: {
          type: 'doc',
          content: [
            {
              type: 'paragraph'
            }
          ]
        }
      })

      this.answers.push(
        {
          is_correct: false,
          editor
        })
    },

    async showRemoveAnswerModal (index) {
      this.$dialog.confirm('Remover resposta')
        .text('Tem certeza que deseja remover esta resposta?')
        .label('Remover')
        .onConfirm(() => {
          this.removeAnswer(index)
        })
        .show()
    },

    removeAnswer (index) {
      if (this.answers[index].is_correct) {
        this.answers[index].is_correct = false
      }

      this.answers[index].editor.destroy()
      this.answers.splice(index, 1)
    },

    setCorrectAnswer (index) {
      this.answers.forEach((item) => { item.is_correct = false })
      this.answers[index].is_correct = true
    },

    async submit () {
      const imageIds = []

      this.statementEditor.state.doc.descendants(function (node) {
        if (node.type.name === 'exam_question_image') {
          imageIds.push(node.attrs.examQuestionImageId)
        }
      })

      for (const answer of this.answers) {
        answer.editor.state.doc.descendants(function (node) {
          if (node.type.name === 'exam_question_image') {
            imageIds.push(node.attrs.examQuestionImageId)
          }
        })
      }

      const data = {
        statement: JSON.stringify(this.statementEditor.getJSON()),
        options: [],
        images_ids: imageIds
      }
      for (const answer of this.answers) {
        data.options.push({
          text: JSON.stringify(answer.editor.getJSON()),
          is_correct: answer.is_correct
        })
      }

      try {
        if (this.questionId) {
          await this.$api.updateExamQuestions(this.questionId, data)
          this.$store.dispatch('setSnackBar', { text: 'Questão atualizada com sucesso.' })
          this.$router.go(-1)
        }
      } catch (e) {
        this.$store.dispatch('setSnackBar', { text: 'Ocorreu um erro ao tentar criar a questão.', color: 'red' })
      }
    },

    async deleteExamQuestions () {
      this.$dialog.confirm('Apagar Questão?')
        .text('Essa ação não pode ser desfeita')
        .label('Apagar')
        .onConfirm(async () => {
          this.loading = true
          try {
            await this.$api.deleteExamQuestions(this.questionId)
            this.$store.dispatch('setSnackBar', { text: 'Questão excluída com sucesso.' })
            this.loading = false
            this.$router.go(-1)
          } catch (e) {
            this.$store.dispatch('setSnackBar', { text: 'Erro ao tentar excluir questão.', color: 'red' })
            this.loading = false
          }
        })
        .show()
    },
    openAddTagDialog () {
      this.$dialog.custom(AddTagDialog).show()
        .then(async (tag) => {
          if (!tag) {
            return
          }
          await this.$api.newExamQuestionTag(this.questionId, tag)
          await this.buildQuestion()
        })
    },
    deleteExamQuestionTags (tagId) {
      this.$dialog.confirm('Remover tag')
        .text('Tem certeza que deseja remover a tag dessa questão ?')
        .label('Remover')
        .onConfirm(async () => {
          try {
            await this.$api.deleteQuestionTag(this.questionId, tagId)
            this.$store.dispatch('setSnackBar', { text: 'Curso removido com sucesso.' })
            await this.buildQuestion()
          } catch (e) {
            this.$store.dispatch('setSnackBar', { text: 'Erro ao tentar remover tag.', color: 'red' })
            this.getErrors(e)
          }
        })
        .show()
    }
  }
}
</script>
